import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App.jsx'
import { NotificationProvider } from './contexts/NotificationProvider'
// LAUNCH:C9.P3.R — shared cart context so routed Supply Lab pages reach the cart
import { CartProvider } from './contexts/CartContext'
import './index.css'

// CON-004: Sentry APM Integration
import * as Sentry from '@sentry/react'

// Analytics imports
import { analytics } from './services/analyticsCollector'
import { replay } from './services/sessionReplay'
import { vitals } from './services/webVitals'

// FE-ERR-001: Error tracking (fallback when Sentry not configured)
import { installGlobalHandlers } from './services/errorTracking'

// Initialize Sentry first (catches early errors)
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const isProduction = import.meta.env.PROD;

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.VITE_ENVIRONMENT || (isProduction ? 'production' : 'development'),

    // Performance monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Only capture replays on errors
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Sample rates
    tracesSampleRate: isProduction ? 0.1 : 1.0, // 10% in prod, 100% in dev
    replaysSessionSampleRate: 0, // Don't capture random sessions
    replaysOnErrorSampleRate: 1.0, // Capture 100% of sessions with errors

    // Trace propagation to backend
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/.*\.(mdmcomics\.com|maestrograding\.ai)/,
      /^https:\/\/.*\.railway\.app/,
    ],

    // Filter out noisy errors
    beforeSend(event) {
      // Ignore ResizeObserver errors (benign)
      if (event.exception?.values?.[0]?.value?.includes('ResizeObserver')) {
        return null;
      }
      // Ignore chunk loading errors (handled by retry logic)
      if (event.exception?.values?.[0]?.value?.includes('Loading chunk')) {
        return null;
      }
      return event;
    },
  });

  console.log('[Sentry] Initialized');
} else {
  // Fall back to custom error tracking when Sentry not configured
  installGlobalHandlers();
  console.log('[ErrorTracking] Sentry not configured, using fallback');
}

// Initialize analytics on app load
analytics.init()

// Start session replay
replay.start()

// Initialize Web Vitals collection
vitals.init()

// Wrap App with Sentry error boundary if initialized
const AppWithErrorBoundary = SENTRY_DSN
  ? Sentry.withProfiler(App)
  : App;

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <BrowserRouter>
      <NotificationProvider>
        <CartProvider>
          <AppWithErrorBoundary />
        </CartProvider>
      </NotificationProvider>
    </BrowserRouter>
  </React.StrictMode>,
)
